import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, Eye, Trash2, Plus, GripVertical,
  Type, Image, Square, Columns, Quote, Link as LinkIcon,
  Video, MapPin, Mail, List, Heading1, FileText, Code,
  Settings, X, Check, ChevronDown, ChevronUp, ShoppingBag,
  Star, Images, HelpCircle, Play
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../App';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Widget types available
const WIDGET_TYPES = [
  { type: 'heading', icon: Heading1, label: 'Heading', description: 'Add a title or subtitle' },
  { type: 'text', icon: Type, label: 'Text Block', description: 'Add paragraph text' },
  { type: 'image', icon: Image, label: 'Image', description: 'Add an image with caption' },
  { type: 'button', icon: Square, label: 'Button', description: 'Call-to-action button' },
  { type: 'columns', icon: Columns, label: 'Two Columns', description: 'Side by side content' },
  { type: 'quote', icon: Quote, label: 'Quote', description: 'Testimonial or quote block' },
  { type: 'video', icon: Video, label: 'Video Embed', description: 'YouTube or Vimeo' },
  { type: 'spacer', icon: Square, label: 'Spacer', description: 'Add vertical spacing' },
  { type: 'divider', icon: Square, label: 'Divider', description: 'Horizontal line' },
  { type: 'list', icon: List, label: 'Feature List', description: 'Bulleted features' },
  { type: 'cta', icon: Mail, label: 'CTA Section', description: 'Call-to-action block' },
  { type: 'financing', icon: LinkIcon, label: 'Financing Embed', description: 'Embed financing iframe' },
  { type: 'html', icon: Code, label: 'Custom HTML', description: 'Raw HTML/embed code' },
  // NEW ADVANCED WIDGETS
  { type: 'product-carousel', icon: ShoppingBag, label: 'Product Carousel', description: 'Showcase products in a slider' },
  { type: 'testimonials', icon: Star, label: 'Testimonials', description: 'Customer reviews carousel' },
  { type: 'image-gallery', icon: Images, label: 'Image Gallery', description: 'Grid of images with lightbox' },
  { type: 'faq', icon: HelpCircle, label: 'FAQ Accordion', description: 'Expandable Q&A section' },
  { type: 'video-hero', icon: Play, label: 'Video Hero', description: 'Full-width video background' },
];

// Default props for each widget type
const getDefaultProps = (type) => {
  switch (type) {
    case 'heading':
      return { text: 'New Heading', level: 'h2', align: 'left' };
    case 'text':
      return { text: 'Add your text content here...', align: 'left' };
    case 'image':
      return { src: 'https://images.unsplash.com/photo-1636018254251-268e77029460?w=800', alt: 'Image', caption: '' };
    case 'button':
      return { text: 'Learn More', url: '/products', variant: 'primary' };
    case 'columns':
      return { left: 'Left column content', right: 'Right column content' };
    case 'quote':
      return { text: 'This is an amazing quote...', author: 'Customer Name' };
    case 'video':
      return { url: 'https://www.youtube.com/embed/dQw4w9WgXcQ' };
    case 'spacer':
      return { height: 48 };
    case 'divider':
      return { style: 'solid' };
    case 'list':
      return { items: ['Feature one', 'Feature two', 'Feature three'] };
    case 'cta':
      return { heading: 'Ready to Get Started?', text: 'Contact us today!', buttonText: 'Contact Us', buttonUrl: '/contact' };
    case 'financing':
      return { embedUrl: '', title: 'Financing Options' };
    case 'html':
      return { code: '<div>Custom HTML here</div>' };
    // NEW ADVANCED WIDGETS
    case 'product-carousel':
      return { 
        title: 'Featured Products', 
        productCount: 4, 
        category: '', 
        showPrice: true, 
        showButton: true,
        autoPlay: true 
      };
    case 'testimonials':
      return { 
        title: 'What Our Customers Say', 
        testimonials: [],  // Add testimonials via Admin → Testimonials
        autoPlay: true,
        showRating: true,
        emptyMessage: 'Customer testimonials will appear here once added in the admin panel.'
      };
    case 'image-gallery':
      return { 
        title: '', 
        images: [
          { src: 'https://images.unsplash.com/photo-1636018254251-268e77029460?w=400', alt: 'Gallery image 1' },
          { src: 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9?w=400', alt: 'Gallery image 2' },
          { src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400', alt: 'Gallery image 3' },
          { src: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400', alt: 'Gallery image 4' }
        ],
        columns: 4,
        gap: 'medium'
      };
    case 'faq':
      return { 
        title: 'Frequently Asked Questions', 
        faqs: [
          { question: 'How long does freeze drying take?', answer: 'A typical batch takes 24-36 hours depending on the food type and moisture content.' },
          { question: 'What foods can I freeze dry?', answer: 'Most foods including fruits, vegetables, meats, dairy, and complete meals. Avoid high-fat or high-sugar items.' },
          { question: 'How long does freeze-dried food last?', answer: 'Properly stored freeze-dried food can last 25+ years while retaining nutritional value.' }
        ]
      };
    case 'video-hero':
      return { 
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', 
        title: 'Discover D-Freeze', 
        subtitle: 'Premium freeze drying technology for your home',
        buttonText: 'Shop Now',
        buttonUrl: '/products',
        overlay: true,
        height: 'large'
      };
    default:
      return {};
  }
};

const PageBuilder = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(!!pageId);
  const [saving, setSaving] = useState(false);
  
  const [pageData, setPageData] = useState({
    title: 'New Page',
    slug: 'new-page',
    published: false,
    meta_description: '',
    sections: [{ id: crypto.randomUUID(), widgets: [] }]
  });

  const [selectedWidget, setSelectedWidget] = useState(null);
  const [showWidgetPanel, setShowWidgetPanel] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (pageId) {
      fetchPage();
    }
  }, [pageId]);

  const fetchPage = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pages/${pageId}`, { headers });
      setPageData(res.data);
    } catch (error) {
      toast.error('Failed to load page');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (pageId) {
        await axios.put(`${API_URL}/api/pages/${pageId}`, pageData, { headers });
        toast.success('Page saved!');
      } else {
        const res = await axios.post(`${API_URL}/api/pages`, pageData, { headers });
        toast.success('Page created!');
        navigate(`/admin/pages/builder/${res.data.id}`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save page';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addWidget = (type) => {
    const newWidget = {
      id: crypto.randomUUID(),
      type,
      props: getDefaultProps(type),
      position: { x: 0, y: pageData.sections[0].widgets.length },
      size: { width: 12, height: 1 }
    };

    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === 0 ? { ...section, widgets: [...section.widgets, newWidget] } : section
      )
    }));

    setSelectedWidget(newWidget.id);
    toast.success(`Added ${type} widget`);
  };

  const updateWidget = (widgetId, newProps) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map(section => ({
        ...section,
        widgets: section.widgets.map(w => 
          w.id === widgetId ? { ...w, props: { ...w.props, ...newProps } } : w
        )
      }))
    }));
  };

  const removeWidget = (widgetId) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map(section => ({
        ...section,
        widgets: section.widgets.filter(w => w.id !== widgetId)
      }))
    }));
    setSelectedWidget(null);
    toast.success('Widget removed');
  };

  const moveWidget = (widgetId, direction) => {
    setPageData(prev => {
      const section = prev.sections[0];
      const index = section.widgets.findIndex(w => w.id === widgetId);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= section.widgets.length) return prev;

      const newWidgets = [...section.widgets];
      [newWidgets[index], newWidgets[newIndex]] = [newWidgets[newIndex], newWidgets[index]];

      return {
        ...prev,
        sections: [{ ...section, widgets: newWidgets }]
      };
    });
  };

  const getSelectedWidgetData = () => {
    for (const section of pageData.sections) {
      const widget = section.widgets.find(w => w.id === selectedWidget);
      if (widget) return widget;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
      </div>
    );
  }

  const selectedWidgetData = getSelectedWidgetData();

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Top Toolbar */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-[#003456]">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-bold text-[#0a0203]" style={{  }}>Page Builder</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Input
            value={pageData.title}
            onChange={(e) => setPageData({ ...pageData, title: e.target.value })}
            className="w-48 text-center font-medium"
            placeholder="Page Title"
            data-testid="page-title-input"
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Published</span>
            <Switch
              checked={pageData.published}
              onCheckedChange={(checked) => setPageData({ ...pageData, published: checked })}
              data-testid="publish-toggle"
            />
          </div>
          {pageData.published && pageId && (
            <Link to={`/page/${pageData.slug}`} target="_blank">
              <Button variant="outline" size="sm" data-testid="preview-btn">
                <Eye className="h-4 w-4 mr-2" /> Preview
              </Button>
            </Link>
          )}
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#003456] hover:bg-[#002B47]"
            data-testid="save-page-btn"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" /> Save
              </>
            )}
          </Button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Widget Panel (Left) */}
        <aside className={`w-72 bg-white border-r border-gray-200 p-4 overflow-y-auto ${showWidgetPanel ? '' : 'hidden'}`}>
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Page Settings</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">URL Slug</label>
                <Input
                  value={pageData.slug}
                  onChange={(e) => setPageData({ ...pageData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="page-url"
                  className="text-sm"
                  data-testid="page-slug-input"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Meta Description</label>
                <Textarea
                  value={pageData.meta_description || ''}
                  onChange={(e) => setPageData({ ...pageData, meta_description: e.target.value })}
                  placeholder="SEO description..."
                  className="text-sm"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Add Widgets</h3>
            <div className="space-y-2">
              {WIDGET_TYPES.map(widget => (
                <button
                  key={widget.type}
                  onClick={() => addWidget(widget.type)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-[#003456] hover:bg-[#dbeef5]/30 transition-all text-left"
                  data-testid={`add-widget-${widget.type}`}
                >
                  <div className="w-10 h-10 bg-[#dbeef5] rounded-lg flex items-center justify-center">
                    <widget.icon className="h-5 w-5 text-[#003456]" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-[#0a0203]">{widget.label}</p>
                    <p className="text-xs text-gray-500">{widget.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Canvas (Center) */}
        <main className="flex-1 p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm min-h-[600px] page-builder-grid">
              {pageData.sections[0].widgets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[600px] text-gray-400">
                  <FileText className="h-16 w-16 mb-4" />
                  <p className="text-lg font-medium">Start building your page</p>
                  <p className="text-sm">Add widgets from the left panel</p>
                </div>
              ) : (
                <div className="p-8 space-y-4">
                  {pageData.sections[0].widgets.map((widget, index) => (
                    <WidgetRenderer
                      key={widget.id}
                      widget={widget}
                      isSelected={selectedWidget === widget.id}
                      onSelect={() => setSelectedWidget(widget.id)}
                      onRemove={() => removeWidget(widget.id)}
                      onMoveUp={() => moveWidget(widget.id, 'up')}
                      onMoveDown={() => moveWidget(widget.id, 'down')}
                      isFirst={index === 0}
                      isLast={index === pageData.sections[0].widgets.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Properties Panel (Right) */}
        {selectedWidgetData && (
          <aside className="w-80 bg-white border-l border-gray-200 p-4 overflow-y-auto animate-slide-in-right">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-[#0a0203]" style={{  }}>
                Edit {selectedWidgetData.type}
              </h3>
              <button onClick={() => setSelectedWidget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <WidgetPropertiesEditor
              widget={selectedWidgetData}
              onUpdate={(props) => updateWidget(selectedWidgetData.id, props)}
            />

            <div className="mt-6 pt-6 border-t border-gray-200">
              <Button
                variant="outline"
                className="w-full text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                onClick={() => removeWidget(selectedWidgetData.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete Widget
              </Button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

// Widget Renderer
const WidgetRenderer = ({ widget, isSelected, onSelect, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const renderContent = () => {
    switch (widget.type) {
      case 'heading':
        const HeadingTag = widget.props.level || 'h2';
        return (
          <HeadingTag className={`text-2xl font-bold text-[#0a0203] ${widget.props.align === 'center' ? 'text-center' : ''}`} style={{  }}>
            {widget.props.text}
          </HeadingTag>
        );
      case 'text':
        return <p className={`text-gray-600 ${widget.props.align === 'center' ? 'text-center' : ''}`}>{widget.props.text}</p>;
      case 'image':
        return (
          <div className="space-y-2">
            <img src={widget.props.src} alt={widget.props.alt} className="rounded-xl w-full max-h-80 object-cover" />
            {widget.props.caption && <p className="text-sm text-gray-500 text-center">{widget.props.caption}</p>}
          </div>
        );
      case 'button':
        return (
          <Button className={widget.props.variant === 'primary' ? 'bg-[#003456] hover:bg-[#002B47]' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}>
            {widget.props.text}
          </Button>
        );
      case 'columns':
        return (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-xl">{widget.props.left}</div>
            <div className="p-4 bg-gray-50 rounded-xl">{widget.props.right}</div>
          </div>
        );
      case 'quote':
        return (
          <blockquote className="border-l-4 border-[#003456] pl-6 py-2">
            <p className="text-xl italic text-gray-700">"{widget.props.text}"</p>
            <p className="text-sm text-gray-500 mt-2">— {widget.props.author}</p>
          </blockquote>
        );
      case 'video':
        return (
          <div className="aspect-video rounded-xl overflow-hidden">
            <iframe src={widget.props.url} className="w-full h-full" title="Video" allowFullScreen />
          </div>
        );
      case 'spacer':
        return <div style={{ height: widget.props.height }} className="bg-gray-100/50 rounded" />;
      case 'divider':
        return <hr className="border-gray-200" />;
      case 'list':
        return (
          <ul className="space-y-2">
            {widget.props.items?.map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                <Check className="h-5 w-5 text-[#003456]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'cta':
        return (
          <div className="bg-[#003456] rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-2" style={{  }}>{widget.props.heading}</h3>
            <p className="mb-4 text-white/80">{widget.props.text}</p>
            <Button className="bg-white text-[#003456] hover:bg-gray-100">{widget.props.buttonText}</Button>
          </div>
        );
      case 'financing':
        return (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
            <LinkIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="font-medium">{widget.props.title || 'Financing Embed'}</p>
            <p className="text-sm text-gray-500">{widget.props.embedUrl || 'Add embed URL in properties'}</p>
          </div>
        );
      case 'html':
        return (
          <div className="bg-gray-100 rounded-xl p-4 font-mono text-sm overflow-x-auto">
            <code>{widget.props.code}</code>
          </div>
        );
      // NEW ADVANCED WIDGETS RENDERERS
      case 'product-carousel':
        return (
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-[#0a0203] mb-4 text-center">{widget.props.title}</h3>
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="aspect-square bg-gray-200 rounded-lg mb-3 flex items-center justify-center">
                    <ShoppingBag className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="font-medium text-sm text-center">Product {i}</p>
                  {widget.props.showPrice && <p className="text-[#003456] text-center text-sm">$2,495 CAD</p>}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 text-center mt-4">Products load dynamically from database</p>
          </div>
        );
      case 'testimonials':
        return (
          <div className="bg-[#F8FAFC] rounded-2xl p-8">
            <h3 className="text-xl font-bold text-[#0a0203] mb-6 text-center">{widget.props.title}</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {widget.props.testimonials?.slice(0, 3).map((t, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  {widget.props.showRating && (
                    <div className="flex gap-1 mb-3">
                      {[...Array(t.rating || 5)].map((_, j) => (
                        <Star key={j} className="h-4 w-4 fill-[#CE8946] text-[#CE8946]" />
                      ))}
                    </div>
                  )}
                  <p className="text-gray-600 italic mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#003456] rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {t.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'image-gallery':
        return (
          <div>
            {widget.props.title && <h3 className="text-xl font-bold text-[#0a0203] mb-4 text-center">{widget.props.title}</h3>}
            <div className={`grid gap-${widget.props.gap === 'large' ? '6' : widget.props.gap === 'small' ? '2' : '4'}`} style={{ gridTemplateColumns: `repeat(${widget.props.columns || 4}, 1fr)` }}>
              {widget.props.images?.map((img, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer" />
                </div>
              ))}
            </div>
          </div>
        );
      case 'faq':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-[#0a0203] mb-6 text-center">{widget.props.title}</h3>
            {widget.props.faqs?.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50">
                  <span className="font-medium">{faq.question}</span>
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                </div>
                <div className="px-4 pb-4 text-gray-600 text-sm border-t border-gray-100 pt-3">
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        );
      case 'video-hero':
        return (
          <div className={`relative rounded-2xl overflow-hidden ${widget.props.height === 'large' ? 'h-96' : widget.props.height === 'small' ? 'h-48' : 'h-64'}`}>
            <div className="absolute inset-0 bg-[#003456]">
              <iframe 
                src={widget.props.videoUrl} 
                className="w-full h-full opacity-50" 
                title="Video Hero"
                allow="autoplay; muted"
              />
            </div>
            {widget.props.overlay && <div className="absolute inset-0 bg-black/40" />}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white p-8">
              <h2 className="text-3xl font-bold mb-2">{widget.props.title}</h2>
              <p className="text-white/80 mb-4 max-w-lg">{widget.props.subtitle}</p>
              {widget.props.buttonText && (
                <Button className="bg-[#CE8946] text-[#0a0203] hover:bg-[#00a9b1]">
                  {widget.props.buttonText}
                </Button>
              )}
            </div>
          </div>
        );
      default:
        return <div className="text-gray-400">Unknown widget type</div>;
    }
  };

  return (
    <div
      onClick={onSelect}
      className={`relative group p-4 rounded-xl border-2 transition-all cursor-pointer ${
        isSelected ? 'border-[#003456] bg-[#dbeef5]/20' : 'border-transparent hover:border-gray-200'
      }`}
      data-testid={`widget-${widget.id}`}
    >
      {/* Controls */}
      <div className={`absolute -top-3 -right-3 flex items-center gap-1 bg-white rounded-lg shadow-sm border border-gray-200 p-1 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          disabled={isFirst}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          disabled={isLast}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="p-1 text-red-400 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {renderContent()}
    </div>
  );
};

// Widget Properties Editor
const WidgetPropertiesEditor = ({ widget, onUpdate }) => {
  switch (widget.type) {
    case 'heading':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Text</label>
            <Input value={widget.props.text} onChange={(e) => onUpdate({ text: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Level</label>
            <select
              value={widget.props.level}
              onChange={(e) => onUpdate({ level: e.target.value })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="h1">H1</option>
              <option value="h2">H2</option>
              <option value="h3">H3</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Alignment</label>
            <select
              value={widget.props.align}
              onChange={(e) => onUpdate({ align: e.target.value })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
            </select>
          </div>
        </div>
      );
    case 'text':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Content</label>
            <Textarea value={widget.props.text} onChange={(e) => onUpdate({ text: e.target.value })} rows={4} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Alignment</label>
            <select
              value={widget.props.align}
              onChange={(e) => onUpdate({ align: e.target.value })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
            </select>
          </div>
        </div>
      );
    case 'image':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Image URL</label>
            <Input value={widget.props.src} onChange={(e) => onUpdate({ src: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Alt Text</label>
            <Input value={widget.props.alt} onChange={(e) => onUpdate({ alt: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Caption</label>
            <Input value={widget.props.caption} onChange={(e) => onUpdate({ caption: e.target.value })} />
          </div>
        </div>
      );
    case 'button':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Button Text</label>
            <Input value={widget.props.text} onChange={(e) => onUpdate({ text: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Link URL</label>
            <Input value={widget.props.url} onChange={(e) => onUpdate({ url: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Style</label>
            <select
              value={widget.props.variant}
              onChange={(e) => onUpdate({ variant: e.target.value })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="primary">Primary (Green)</option>
              <option value="secondary">Secondary (Gray)</option>
            </select>
          </div>
        </div>
      );
    case 'quote':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Quote Text</label>
            <Textarea value={widget.props.text} onChange={(e) => onUpdate({ text: e.target.value })} rows={3} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Author</label>
            <Input value={widget.props.author} onChange={(e) => onUpdate({ author: e.target.value })} />
          </div>
        </div>
      );
    case 'video':
      return (
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Video Embed URL</label>
          <Input value={widget.props.url} onChange={(e) => onUpdate({ url: e.target.value })} placeholder="https://youtube.com/embed/..." />
        </div>
      );
    case 'spacer':
      return (
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Height (px)</label>
          <Input type="number" value={widget.props.height} onChange={(e) => onUpdate({ height: parseInt(e.target.value) })} />
        </div>
      );
    case 'list':
      return (
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Items (one per line)</label>
          <Textarea
            value={widget.props.items?.join('\n') || ''}
            onChange={(e) => onUpdate({ items: e.target.value.split('\n').filter(Boolean) })}
            rows={5}
          />
        </div>
      );
    case 'cta':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Heading</label>
            <Input value={widget.props.heading} onChange={(e) => onUpdate({ heading: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Text</label>
            <Textarea value={widget.props.text} onChange={(e) => onUpdate({ text: e.target.value })} rows={2} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Button Text</label>
            <Input value={widget.props.buttonText} onChange={(e) => onUpdate({ buttonText: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Button URL</label>
            <Input value={widget.props.buttonUrl} onChange={(e) => onUpdate({ buttonUrl: e.target.value })} />
          </div>
        </div>
      );
    case 'financing':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Title</label>
            <Input value={widget.props.title} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Embed URL</label>
            <Input value={widget.props.embedUrl} onChange={(e) => onUpdate({ embedUrl: e.target.value })} placeholder="https://..." />
          </div>
        </div>
      );
    case 'html':
      return (
        <div>
          <label className="text-xs text-gray-500 mb-1 block">HTML Code</label>
          <Textarea
            value={widget.props.code}
            onChange={(e) => onUpdate({ code: e.target.value })}
            rows={6}
            className="font-mono text-sm"
          />
        </div>
      );
    case 'columns':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Left Column</label>
            <Textarea value={widget.props.left} onChange={(e) => onUpdate({ left: e.target.value })} rows={3} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Right Column</label>
            <Textarea value={widget.props.right} onChange={(e) => onUpdate({ right: e.target.value })} rows={3} />
          </div>
        </div>
      );
    // NEW ADVANCED WIDGETS EDITORS
    case 'product-carousel':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Section Title</label>
            <Input value={widget.props.title} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Number of Products</label>
            <select
              value={widget.props.productCount}
              onChange={(e) => onUpdate({ productCount: parseInt(e.target.value) })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="3">3 Products</option>
              <option value="4">4 Products</option>
              <option value="6">6 Products</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Filter by Category</label>
            <Input value={widget.props.category} onChange={(e) => onUpdate({ category: e.target.value })} placeholder="e.g., freeze-dryer" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Show Price</span>
            <Switch checked={widget.props.showPrice} onCheckedChange={(v) => onUpdate({ showPrice: v })} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Show Add to Cart Button</span>
            <Switch checked={widget.props.showButton} onCheckedChange={(v) => onUpdate({ showButton: v })} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Auto-play Carousel</span>
            <Switch checked={widget.props.autoPlay} onCheckedChange={(v) => onUpdate({ autoPlay: v })} />
          </div>
        </div>
      );
    case 'testimonials':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Section Title</label>
            <Input value={widget.props.title} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Show Star Ratings</span>
            <Switch checked={widget.props.showRating} onCheckedChange={(v) => onUpdate({ showRating: v })} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Auto-play Carousel</span>
            <Switch checked={widget.props.autoPlay} onCheckedChange={(v) => onUpdate({ autoPlay: v })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-2 block">Testimonials (JSON format)</label>
            <Textarea 
              value={JSON.stringify(widget.props.testimonials, null, 2)} 
              onChange={(e) => {
                try {
                  onUpdate({ testimonials: JSON.parse(e.target.value) });
                } catch {}
              }}
              rows={8}
              className="font-mono text-xs"
              placeholder='[{"name": "Name", "location": "City", "text": "Review", "rating": 5}]'
            />
          </div>
        </div>
      );
    case 'image-gallery':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Gallery Title (optional)</label>
            <Input value={widget.props.title} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Columns</label>
            <select
              value={widget.props.columns}
              onChange={(e) => onUpdate({ columns: parseInt(e.target.value) })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="2">2 Columns</option>
              <option value="3">3 Columns</option>
              <option value="4">4 Columns</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Gap Size</label>
            <select
              value={widget.props.gap}
              onChange={(e) => onUpdate({ gap: e.target.value })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-2 block">Images (JSON format)</label>
            <Textarea 
              value={JSON.stringify(widget.props.images, null, 2)} 
              onChange={(e) => {
                try {
                  onUpdate({ images: JSON.parse(e.target.value) });
                } catch {}
              }}
              rows={8}
              className="font-mono text-xs"
              placeholder='[{"src": "url", "alt": "description"}]'
            />
          </div>
        </div>
      );
    case 'faq':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Section Title</label>
            <Input value={widget.props.title} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-2 block">FAQs (JSON format)</label>
            <Textarea 
              value={JSON.stringify(widget.props.faqs, null, 2)} 
              onChange={(e) => {
                try {
                  onUpdate({ faqs: JSON.parse(e.target.value) });
                } catch {}
              }}
              rows={10}
              className="font-mono text-xs"
              placeholder='[{"question": "Q?", "answer": "A."}]'
            />
          </div>
        </div>
      );
    case 'video-hero':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Video Embed URL</label>
            <Input value={widget.props.videoUrl} onChange={(e) => onUpdate({ videoUrl: e.target.value })} placeholder="https://youtube.com/embed/..." />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Overlay Title</label>
            <Input value={widget.props.title} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Subtitle</label>
            <Textarea value={widget.props.subtitle} onChange={(e) => onUpdate({ subtitle: e.target.value })} rows={2} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Button Text</label>
            <Input value={widget.props.buttonText} onChange={(e) => onUpdate({ buttonText: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Button URL</label>
            <Input value={widget.props.buttonUrl} onChange={(e) => onUpdate({ buttonUrl: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Height</label>
            <select
              value={widget.props.height}
              onChange={(e) => onUpdate({ height: e.target.value })}
              className="w-full rounded-md border border-gray-200 p-2"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Dark Overlay</span>
            <Switch checked={widget.props.overlay} onCheckedChange={(v) => onUpdate({ overlay: v })} />
          </div>
        </div>
      );
    default:
      return <p className="text-gray-500">No properties available</p>;
  }
};

export default PageBuilder;
